import {
  CircularProgress,
  Menu,
  menuClasses,
  MenuItem,
  styled,
  Typography,
  typographyClasses,
  TypographyProps,
} from '@mui/material';
import { IconMore } from 'assets/img';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { useToggle } from 'shared/hooks';
import { ProviderFactory } from 'shared/providers/provider-factory';
import { LwIconButton } from 'redesign';
import { ContextMenuProps } from './context-menu.types';

const [ContextMenuProvider, useContextMenuProvider] = ProviderFactory<() => void>(
  'useContextMenu can only be used within ContextMenuContext'
);

const ContextMenuButton = ({
  loading,
  children,
  menuId,
  buttonClass: buttonClassName,
  color,
  disabled,
}: React.PropsWithChildren<ContextMenuProps>) => {
  const anchorEl = useRef<HTMLButtonElement>(null);
  const { on: opened, toggle } = useToggle();

  return (
    <ContextMenuProvider value={toggle}>
      <LwIconButton
        iconColorMode="stroke"
        color={color ? color : 'default'}
        aria-controls={menuId}
        aria-haspopup="true"
        className={clsx(buttonClassName)}
        onClick={toggle}
        disabled={loading || disabled}
        ref={anchorEl}
        data-testid="action-icon-button"
      >
        {loading ? <CircularProgress size={16} /> : <IconMore />}
      </LwIconButton>

      <StyledMenu
        id={menuId}
        open={opened}
        onClose={toggle}
        anchorEl={anchorEl.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {children}
      </StyledMenu>
    </ContextMenuProvider>
  );
};

const ContextMenuItem = ({
  children,
  onClick,
  ...props
}: React.PropsWithChildren<
  { loading?: boolean } & Pick<React.ComponentProps<typeof MenuItem>, 'onClick' | 'disabled'>
>) => {
  const closeContextMenu = useContextMenuProvider();

  const handleClick: React.MouseEventHandler<HTMLLIElement> = (e) => {
    onClick?.(e);
    closeContextMenu();
  };

  return (
    <StyledMenuItem {...props} onClick={handleClick}>
      <StyledTypography component="span">{children}</StyledTypography>
    </StyledMenuItem>
  );
};

const StyledMenu = styled(Menu)(({ theme }) => ({
  marginTop: theme.spacing(4),

  [`& .${menuClasses.paper}`]: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  '&:hover': {
    backgroundColor: theme.palette.lwPrimaryAccentLight[5],
    borderRadius: '12px',
  },
}));

const StyledTypography = styled(Typography)<TypographyProps>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'Urbanist',
  fontWeight: 500,
  fontSize: '16px',
  padding: 0,
  gap: '10px',

  [`&.${typographyClasses.root}`]: {
    color: theme.palette.lwPrimaryAccentDark[100],
  },

  '&:hover': {
    color: theme.palette.lwPrimaryAccentLight[100],
  },
}));

export { ContextMenuButton, ContextMenuItem };
